import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";

import useEntity from "../../hooks/useEntity";
import useData from "../../hooks/useData";

import UserModal from "../modals/UserModal";
import Dialog_Flex from "../ui/Dialog_Flex";

import Page from "./Page";
import EntityList from "./EntityList";

import Filter from "../ui/Filter";
import Pagination from "../elements/Pagination";
import Loading from "../ui/Loading";

const Users = () => {
  const currentUserRole = useSelector((state) => state.authentication.role);
  const isSysAdmin = currentUserRole === "sysadmin";

  const navigate = useNavigate();
  const location = useLocation();

  const [openUserModal, setOpenUserModal] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [filter, setFilter] = useState(null);
  const [activeFilter, setActiveFilter] = useState("active");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Missing coordinates dialog state
  const [openMissingDialog, setOpenMissingDialog] = useState(false);
  const [missingUsers, setMissingUsers] = useState([]);
  const [missingSelected, setMissingSelected] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);
  const hasMountedSearchEffect = useRef(false);

  const {
    getPage,
    getEntityList,
    setEntity,
    entityName,
    entityList,
    entitySchema,
    recordCount,
  } = useEntity();

  const { executeAction } = useData();

  const getDisplayName = (user) => {
    const legalName = String(user?.legalName || "").trim();
    const preferredName = String(user?.name || "").trim();
    return legalName || preferredName || "-";
  };

  const displayEntityList = useMemo(
    () =>
      (Array.isArray(entityList) ? entityList : []).map((user) => ({
        ...user,
        name: getDisplayName(user),
      })),
    [entityList],
  );

  const roleFilterItems = [
    { id: "admin", name: "Admin" },
    { id: "hr", name: "HR" },
    { id: "supervisor", name: "Supervisor" },
    { id: "rep", name: "Rep" },
    { id: "client", name: "Client" },
    { id: "finance", name: "Finance" },
    { id: "qa", name: "QA" },
  ];

  const activeFilterItems = [
    { id: "active", name: "Active" },
    { id: "inactive", name: "Inactive" },
    { id: "all", name: "All" },
  ];

  const withActiveFilter = (
    sourceFilter = filter,
    sourceActiveFilter = activeFilter,
  ) => {
    const baseFilter = { ...(sourceFilter || {}) };

    delete baseFilter.isActive;

    if (sourceActiveFilter === "active") {
      baseFilter.isActive = true;
    }

    if (sourceActiveFilter === "inactive") {
      baseFilter.isActive = false;
    }

    return baseFilter;
  };

  const getEffectiveFilter = (
    sourceFilter = filter,
    sourceActiveFilter = activeFilter,
  ) => {
    const activeFiltered = withActiveFilter(sourceFilter, sourceActiveFilter);

    if (isSysAdmin) return activeFiltered;

    const baseFilter = { ...activeFiltered };

    if (baseFilter.role) {
      return baseFilter;
    }

    return {
      ...baseFilter,
      role: { $ne: "sysadmin" },
    };
  };

  const searchText = String(filter?.$text?.$search || "");

  useEffect(() => {
    setEntity("User");
    if (location && location.state) {
      if (location.state.filter) {
        setFilter(location.state.filter);

        if (location.state.filter.isActive === true) {
          setActiveFilter("active");
        } else if (location.state.filter.isActive === false) {
          setActiveFilter("inactive");
        } else {
          setActiveFilter("all");
        }
      }
      if (location.state.pageNumber) setPageNumber(location.state.pageNumber);
    }
  }, []);

  useEffect(() => {
    if (!entityName) return;

    getPage(
      pageNumber,
      recordsPerPage,
      getEffectiveFilter(filter),
      orderBy,
      order,
    );
  }, [entityName]);

  useEffect(() => {
    if (!entityName) return;
    getPage(
      pageNumber,
      recordsPerPage,
      getEffectiveFilter(filter),
      orderBy,
      order,
    );
  }, [
    pageNumber,
    recordsPerPage,
    currentUserRole,
    activeFilter,
    orderBy,
    order,
  ]);

  useEffect(() => {
    if (!entityName) return;

    if (!hasMountedSearchEffect.current) {
      hasMountedSearchEffect.current = true;
      return;
    }

    const timeoutId = setTimeout(async () => {
      setPageNumber(0);
      await getPage(
        0,
        recordsPerPage,
        getEffectiveFilter(filter),
        orderBy,
        order,
      );
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchText]);

  const handleSearch = async () => {
    setPageNumber(0);
    await getPage(
      0,
      recordsPerPage,
      getEffectiveFilter(filter),
      orderBy,
      order,
    );
  };

  const handleClearFilter = async () => {
    setFilter(null);
    setActiveFilter("active");
    setPageNumber(0);
    await getPage(
      0,
      recordsPerPage,
      getEffectiveFilter(null, "active"),
      orderBy,
      order,
    );
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const handleRecordsPerPageChange = (newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
  };

  const handleActiveFilterChange = async (newValue) => {
    setActiveFilter(newValue);
    setPageNumber(0);
    await getPage(
      0,
      recordsPerPage,
      getEffectiveFilter(filter, newValue),
      orderBy,
      order,
    );
  };

  const handleSortChange = async (newOrderBy, newOrder) => {
    setOrderBy(newOrderBy);
    setOrder(newOrder);
    setPageNumber(0);
    await getPage(
      0,
      recordsPerPage,
      getEffectiveFilter(filter),
      newOrderBy,
      newOrder,
    );
  };

  const handleToolbarItemClick = async (item) => {
    switch (item) {
      case "add":
        setOpenUserModal(true);
        break;
      case "export":
        navigate("/userexports");
        break;
      case "missingCoords":
        await loadMissingUsers();
        setOpenMissingDialog(true);
        break;
    }
  };

  const loadMissingUsers = async () => {
    setMissingLoading(true);
    try {
      const result = await executeAction({
        entityName: "User",
        action: "getMissingCoordinates",
        data: {},
      });
      if (result?.isSuccessful && Array.isArray(result.data)) {
        setMissingUsers(
          isSysAdmin
            ? result.data
            : result.data.filter((user) => user.role !== "sysadmin"),
        );
        setMissingSelected([]);
      }
    } catch (err) {
      console.error("Error loading missing coordinates:", err);
    } finally {
      setMissingLoading(false);
    }
  };

  const handleMissingSelectAll = (event) => {
    if (event.target.checked) {
      setMissingSelected(missingUsers.map((u) => u.id));
    } else {
      setMissingSelected([]);
    }
  };

  const handleMissingSelect = (userId) => {
    setMissingSelected((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleMissingDialogAction = async (action) => {
    if (action === "cancel") {
      setOpenMissingDialog(false);
      return;
    }

    if (action === "apply") {
      try {
        await executeAction({
          entityName: "User",
          action: "geocodeUsers",
          data: {
            userIds: missingSelected,
          },
          displayMessage: true,
        });
        setOpenMissingDialog(false);
      } catch (err) {
        console.error("Error queueing geocoding:", err);
      }
    }
  };

  const handleCloseModal = () => {
    setSelectedUserId(null);
    setOpenUserModal(false);
  };

  const handleRowClick = (column, id) => {
    if (!id) return;

    setSelectedUserId(id);
    setOpenUserModal(true);

    // navigate(`/user`, { state: { userId: id, filter, pageNumber } });
  };

  const handleSave = async (returnData) => {
    setOpenUserModal(false);
    if (returnData) {
      setFilter(returnData.filter);
      setPageNumber(returnData.pageNumber);
    }
    await getPage(
      pageNumber,
      recordsPerPage,
      getEffectiveFilter(filter),
      orderBy,
      order,
    );
    //await getEntityList();
  };

  if (!entityList) return <Loading />;
  return (
    <>
      <Page
        pageName="users"
        onSearch={handleSearch}
        onToolItemClick={handleToolbarItemClick}
        includeToolbar
        items={[
          { name: "add", iconName: "AddCircleOutline" },
          { name: "export", iconName: "FileDownload" },
          { name: "missingCoords", iconName: "GpsOff" },
        ]}
      >
        <Filter
          fields={["province", "role", "search", "activeStatus"]}
          filter={filter}
          roleItems={roleFilterItems}
          activeStatusValue={activeFilter}
          activeStatusItems={activeFilterItems}
          onActiveStatusChange={handleActiveFilterChange}
          onFilterChange={(newValue) => handleFilterChange(newValue)}
          onSearch={handleSearch}
          onClear={handleClearFilter}
        />

        <Pagination
          totalCount={recordCount}
          styles={{ marginTop: "5px" }}
          pageNumber={pageNumber}
          recordsPerPage={recordsPerPage}
          onPageChange={handlePageChange}
          onRecordsPerPageChange={handleRecordsPerPageChange}
        />

        <EntityList
          entityList={displayEntityList}
          entitySchema={entitySchema}
          onRowClick={handleRowClick}
          onSortChange={handleSortChange}
          orderBy={orderBy}
          order={order}
          columns={[
            { name: "name", clickable: true },
            {
              name: "syndicatedTerritories",
              label: "territory",
              clickable: true,
            },
            { name: "address.province", clickable: true },
            { name: "address.city", clickable: true },
            { name: "email", clickable: true },
          ]}
        />
      </Page>

      <UserModal
        open={openUserModal}
        onClose={handleCloseModal}
        onSave={handleSave}
        userId={selectedUserId}
      />

      <Dialog_Flex
        open={openMissingDialog}
        title={"Geocode Missing Coordinates"}
        message={
          "Select users to geocode addresses. Leave empty to process all."
        }
        actions={[
          { name: "cancel", label: "cancel" },
          { name: "apply", label: "apply" },
        ]}
        onAction={handleMissingDialogAction}
      >
        {missingLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : missingUsers.length === 0 ? (
          <Typography>No users missing coordinates</Typography>
        ) : (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={missingSelected.length === missingUsers.length}
                  indeterminate={
                    missingSelected.length > 0 &&
                    missingSelected.length < missingUsers.length
                  }
                  onChange={handleMissingSelectAll}
                />
              }
              label="Select All"
            />
            <List sx={{ maxHeight: 320, overflow: "auto" }}>
              {missingUsers.map((user) => (
                <ListItem
                  key={user.id}
                  dense
                >
                  <Checkbox
                    checked={missingSelected.includes(user.id)}
                    onChange={() => handleMissingSelect(user.id)}
                  />
                  <ListItemText
                    primary={getDisplayName(user)}
                    secondary={[
                      user.address?.streetAddress,
                      user.address?.city,
                      user.address?.province,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </Dialog_Flex>
    </>
  );
};

export default Users;
